/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { IFooterCategoryViewProps } from '@msdyn365-commerce-modules/footer/src/modules/footer-category/./footer-category';
import { addThrottledEvent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { ICoreContext } from '@msdyn365-commerce/core';
import React, { ReactNode, useEffect, useState } from 'react';

import classnames from 'classnames';
import { IFooterCategoryProps } from '../definition-extensions/footer-category.ext.props.autogenerated';

export const FooterCategoryView: React.FC<IFooterCategoryViewProps & IFooterCategoryProps<{}>> = props => {
    /* VSI Customization - START */
    /* isOpen shows if a collapse is open or not */
    const [isOpen, setIsOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    /* VSI Customization - END */
    const {
        categories,
        FooterList,
        FooterListItem,
        config: { collapseItems }
    } = props;
    if (!categories || !categories.length) {
        props.context.telemetry.error('Footer content is empty, module wont render');
        return null;
    }

    /* VSI Customization - START */
    /*  condition either to show or hide the header  */
    if (props.context.request.query!.onlyBody && props.context.request.query!.onlyBody === 'true') {
        return null;
    }
    /* useEffect will update footer according to device type */
    useEffect(() => {
        setMobileView();
        window && addThrottledEvent(window, 'resize', setMobileView);
    });

    const setMobileView = () => {
        const _isMobile = isMobileView(props.context);
        setIsMobile(_isMobile);
    };
    /* Click on collapse will toggle it */
    const _handleCollapse = (event: MouseEvent) => {
        setIsOpen(!isOpen);
    };
    /* collapse will only be shown on mobile */
    let _collapse = isMobile ? true : false;
    /* On mobile, category with Collapse value false will not be shown as collapse */
    if (_collapse) {
        _collapse = collapseItems === false ? false : true;
    }
    /* VSI Customization - END */
    return (
        <React.Fragment>
            {/* VSI Customization - START */}
            {_collapse ? (
                <Module {...FooterList}>
                    <Node
                        {...FooterListItem}
                        className={classnames(FooterListItem.className, isOpen ? 'Show' : 'hide')}
                        onClick={_handleCollapse}
                    >
                        {categories[0]}
                    </Node>

                    <div className={isOpen ? 'slide-down' : 'slide-up'}>
                        {categories.map((category: ReactNode, index: number) => {
                            if (index > 0) {
                                return (
                                    <Node {...FooterListItem} key={index}>
                                        {category}
                                    </Node>
                                );
                            }
                            return null;
                        })}
                    </div>
                </Module>
            ) : (
                <Module {...FooterList}>
                    {categories.map((category: ReactNode, index: number) => {
                        return (
                            <Node {...FooterListItem} key={index}>
                                {category}
                            </Node>
                        );
                    })}
                </Module>
            )}
            {/* VSI Customization - END */}
        </React.Fragment>
    );
};

/* VSI Customization - START */
const isMobileView = (context: ICoreContext): boolean => {
    if (typeof window !== undefined && window.innerWidth) {
        const gridSettings = context.request.gridSettings;
        if (gridSettings) {
            if (gridSettings.xs && window.innerWidth < gridSettings.xs.w) {
                return true;
            } else if (gridSettings.sm && window.innerWidth < gridSettings.sm.w) {
                return true;
            } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                return false;
            } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                return false;
            } else {
                return false;
            }
        }
    }

    return false;
};
/* VSI Customization - END */

export default FooterCategoryView;
